import DropdownSelection from "@app-components/dropdown-selection/dropdown-selection.vue";
import ReservationFormBase from "@app-components/reservation-form-base/reservation-form-base.vue";
import usei18n from "@app-i18n/index";
import useAttractionsRestClient from "@app-rest/attractions";
import { IAttraction } from "@app-rest/attractions.interfaces";
import { IPlayersQuantity, IPlayerType } from "@app-rest/systems.interfaces";
import { home } from "@app-router/routes/reservations";
import useLangStore from "@app-store/lang";
import useSystemStore from "@app-store/system";
import { dateKey, format, stringToDate, stringToDateUTC } from "@app-utilities/dates";
import { useRuleBowlersRequired, useRuleDateRequired } from "@app-utilities/rules";
import { QFormFieldDate, QFormFieldPlayers, QOverlay } from "@qamf/lighthouse";
import { isBefore, set, startOfDay, subDays } from "date-fns";
import { computed, defineComponent, onBeforeMount, onMounted, PropType, ref, useTemplateRef, watchEffect } from "vue";
import { useRouter } from "vue-router";

export interface IAttractionReservationFormData {
	attraction: IDropDownOption | null;
	datetime: Date | null;
	players: IPlayersQuantity[];
}
interface IDropDownOption {
	text: string;
	isDisabled?: boolean;
	value: string | number;
	href?: string
}

export interface IPlayersTypes {
	MaxPlayers: number;
	MinPlayers?: number;
	Types: IPlayerType[];
}

export const playerTypesData = {
	MaxPlayers: 1,
	MinPlayers: 1,
	Types: [
		{
			Id: 1,
			Name: "Players",
			Active: true
		}
	]
};

export default defineComponent({
	name: "AttractionsReservationForm",
	components: {
		QOverlay,
		DropdownSelection,
		QFormFieldDate,
		QFormFieldPlayers,
		ReservationFormBase
	},
	props: {
		initialFormData: { type: Object as PropType<IAttractionReservationFormData>, default: null },
		compact: { type: Boolean, default: false }
	},
	emits: ["submit"],
	setup(props, { emit }) {
		const { translateKey } = usei18n();
		const router = useRouter();
		const langStore = useLangStore();
		const systemStore = useSystemStore();
		const reservationFormBase = useTemplateRef<InstanceType<typeof ReservationFormBase> | null>("reservationFormBase");
		const dropdown = useTemplateRef<InstanceType<typeof DropdownSelection> | null>("dropdown");
		const datePicker = useTemplateRef<InstanceType<typeof QFormFieldDate> | null>("datePicker");
		const playersPicker = useTemplateRef<InstanceType<typeof QFormFieldPlayers> | null>("playersPicker");
		const selectedAttraction = ref<IDropDownOption | null>(null);
		const datePicked = ref<Date | null>(new Date());
		const players = ref<IPlayersQuantity[]>([]);
		const dateFnsLocale = ref<Locale | undefined>();
		const disabledDates = ref<Date[] | undefined>();
		const playerTypes = ref<IPlayersTypes | null>(null);
		const filteredAttractions = ref<IAttraction[] | null>(null);
		const attractionOptions = ref<IDropDownOption[]>();

		const isDateValid = computed(() => {
			return Boolean(datePicked.value);
		});
		const isPlayersValid = computed(() => {
			return Boolean(players.value.length);
		});

		const minutesDayChangeTime = computed(() => {
			if (!systemStore.info || !systemStore.info.DayChangeTime) return;

			const timeSep = ":";
			return +(systemStore.info.DayChangeTime.split(timeSep)[1]);
		});

		const hoursDayChangeTime = computed(() => {
			if (!systemStore.info || !systemStore.info.DayChangeTime) return;

			const timeSep = ":";
			return +(systemStore.info.DayChangeTime.split(timeSep)[0]);
		});

		const isPreviousDay = (date: Date) => {
			const dayChangeTime = set(date, {
				hours: hoursDayChangeTime.value,
				minutes: minutesDayChangeTime.value
			});

			return isBefore(date, dayChangeTime);
		};

		const minDateCalendar = computed(() => {
			const now = new Date();
			const minEvaluated = isPreviousDay(now) ? subDays(now, 1) : now;
			return minEvaluated;
		});
		const maxDateCalendar = computed(() => {
			const lastBookableDay = systemStore.lastBookableDay;
			if (lastBookableDay) return startOfDay(new Date(lastBookableDay));

			return undefined;
		});
		const openingDates = computed(() => {
			return systemStore.openingTimes?.Dates ?? [];
		});
		const ensureOpeningsFrom = async(date: Date) => {
			await systemStore.ensureOpenings(date);
		};
		const retrieveDisabledDates = () => {
			if (openingDates.value.length === 0) return undefined;

			return openingDates.value.filter(date => date.IsOpen === false).map(date => stringToDate(date.Date)).filter((el) => el !== undefined);
		};
		const getOpeningDayFrom = (date: Date) => {
			const formatted = dateKey(date);
			const dateInfo = systemStore.getOpeningDay(formatted);
			if (dateInfo?.IsOpen) return startOfDay(date);
			else {
				const nextOpen = systemStore.getNextOpenDay(date);
				if (!nextOpen) return null;
				const openDate = stringToDateUTC(nextOpen.Date);
				if (!openDate) return null;
				return startOfDay(openDate);
			}
		};

		const attractionLabel = computed(() => {
			return selectedAttraction.value ? `${selectedAttraction.value.text}` : "";
		});

		const dateLabel = computed(() => {
			const attractionLabel = selectedAttraction.value ? `${selectedAttraction.value.text} | ` : "";
			const dateLabel = datePicked.value ? `${format(datePicked.value, "dd/MM/yyyy")}` : "";
			return `${attractionLabel} ${dateLabel}`;
		});

		const totalPlayers = computed(() => {
			return players.value.reduce((accumulator, type) => accumulator + type.Quantity, 0);
		});
		const playerLabel = computed(() => {
			const label = totalPlayers.value === 1
				? translateKey("label_player")
				: translateKey("label_players");

			const attractionLabel = selectedAttraction.value ? `${selectedAttraction.value.text} | ` : "";
			const dateLabel = datePicked.value ? `${format(datePicked.value, "dd/MM/yyyy")} | ` : "";
			return `${attractionLabel} ${dateLabel} ${totalPlayers.value} ${label}`;
		});
		const formatDate = (ymd: Date | null) => {
			return ymd ? format(ymd, "EEEE, MMM d") : "";
		};
		const todayLabel = computed(() => {
			return translateKey("label_today");
		});

		const hideOverlay = () => {
			reservationFormBase.value?.hideOverlay();
		};
		const showOverlay = () => {
			reservationFormBase.value?.showOverlay();
		};
		const closeAllPopOver = () => {
			dropdown.value?.hidePopover();
			datePicker.value?.hidePopover();
			playersPicker.value?.hidePopover();
			hideOverlay();
		};

		const goToDropdown = () => {
			closeAllPopOver();
			dropdown.value?.openPopover();
		};

		const goToDatePicker = () => {
			closeAllPopOver();
			datePicker.value?.openPopover();
		};

		const goToPlayers = () => {
			closeAllPopOver();
			playersPicker.value?.openPopover();
		};

		const trySubmit = async(): Promise<void> => {
			closeAllPopOver();
			if (!selectedAttraction.value)
				return dropdown.value?.openPopover();

			if (!datePicked.value)
				return datePicker.value?.openPopover();

			if (!players.value.length)
				return playersPicker.value?.openPopover();

			if (
				!selectedAttraction.value
				|| !datePicked.value
                || !players.value.length
			)
				return;

			const currentFormData = {
				attraction: selectedAttraction.value,
				datetime: datePicked.value,
				players: players.value
			};

			emit("submit", currentFormData);
		};

		watchEffect(() => {
			if (selectedAttraction.value?.text && filteredAttractions.value?.length) {
				const currentAttraction = filteredAttractions.value.find(attr => {
					return typeof selectedAttraction.value?.value === "number" && attr.Id === selectedAttraction.value.value;
				});

				const currentCapacity = currentAttraction ? Math.max(...currentAttraction.Resources.map(resource => resource.MaxCapacity)) : 0;
				playerTypes.value = {
					...playerTypesData,
					MaxPlayers: currentCapacity
				};

				if (currentCapacity < players.value[0].Quantity)
					players.value = [{ Id: 1, Name: "Players", Active: true, Quantity: 2 }];
			}
		});

		onBeforeMount(() => {
			useRuleDateRequired("date-required");
			useRuleBowlersRequired("players-required");
		});
		onMounted(async() => {
			dateFnsLocale.value = langStore.libDictionary.get("date-fns");

			const rest = useAttractionsRestClient();
			if (systemStore.info) {
				const elements = await rest.getAttractionsList(systemStore.info?.Id);
				const filteredElements = elements.data ? elements.data.Elements.filter(el => el.Status === "Active") : [];

				filteredAttractions.value = filteredElements;

				const formattedElements = filteredElements
					.map(el => ({
						text: el.Title,
						value: el.Id
					}));
				attractionOptions.value = formattedElements.length
					? [...formattedElements, {
						text: translateKey("want_to_reserve_bowling"),
						value: "want_to_reserve_bowling",
						href: router.resolve({ name: home }).href
					}]
					: [];
				selectedAttraction.value = props.initialFormData.attraction ?? formattedElements[0];

				players.value = props.initialFormData.players.length 
					? [...props.initialFormData.players]
					: [{ Id: 1, Name: "Players", Active: true, Quantity: 2 }];
			}

			const datetime = props.initialFormData?.datetime ?? new Date();
			const yesterday = subDays(datetime, 1);
			await ensureOpeningsFrom(yesterday);
			datePicked.value = getOpeningDayFrom(datetime);
			disabledDates.value = retrieveDisabledDates();
		});

		return {
			reservationFormBase,
			dropdown,
			datePicker,
			playersPicker,
			selectedAttraction,
			datePicked,
			players,
			attractionOptions,
			dateFnsLocale,
			disabledDates,
			isDateValid,
			isPlayersValid,
			minDateCalendar,
			maxDateCalendar,
			playerTypes,
			attractionLabel,
			dateLabel,
			totalPlayers,
			playerLabel,
			formatDate,
			todayLabel,
			showOverlay,
			hideOverlay,
			closeAllPopOver,
			goToDropdown,
			goToDatePicker,
			goToPlayers,
			trySubmit
		};
	}
});
